import { useState } from 'react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { BookingPushLogsList } from '@/components/booking-push/BookingPushLogsList';
import { useBookingPushLogs } from '@/api/booking-push';
import { usePartnerAccounts } from '@/api/partner-accounts';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'pushing', label: 'Pushing' },
  { value: 'success', label: 'Success' },
  { value: 'failed', label: 'Failed' },
  { value: 'escalated', label: 'Escalated' },
];

export function BookingPushLogsPage(): JSX.Element {
  const [partnerAccountId, setPartnerAccountId] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [page, setPage] = useState(1);

  const { data: partnerAccounts } = usePartnerAccounts();
  const { data, isLoading } = useBookingPushLogs({
    partnerAccountId: partnerAccountId || undefined,
    status: status || undefined,
    page,
    limit: 20,
  });

  const logs = data?.data ?? [];
  const pagination = data?.pagination;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Booking Push Logs</h1>
        <p className="text-muted-foreground">
          Monitor booking synchronization with partner platforms
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <Select
              value={partnerAccountId}
              onValueChange={(value) => {
                setPartnerAccountId(value === 'all' ? '' : value);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[200px]" data-testid="filter-partner-account">
                <SelectValue placeholder="All Partner Accounts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Partner Accounts</SelectItem>
                {partnerAccounts?.map((account) => (
                  <SelectItem key={account.id} value={account.id}>
                    {account.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={status}
              onValueChange={(value) => {
                setStatus(value === 'all' ? '' : value);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[150px]" data-testid="filter-status">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <BookingPushLogsList logs={logs} isLoading={isLoading} />

          {pagination && pagination.totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Showing {logs.length} of {pagination.total} logs
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setPage((p) => Math.max(1, p - 1)); }}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setPage((p) => Math.min(pagination.totalPages, p + 1)); }}
                  disabled={page >= pagination.totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
